import React, { useEffect, useState } from "react";
import ClientLayout from "../../../components/client/ClientLayout";
import RequireClientAdmin from "../../../components/client/RequireClientAdmin";
import AdminSelect from "../../../components/AdminSelect";
import { clientApi } from "../../../api";

/**
 * Client-portal Communications configuration.
 *
 *   • AGM acknowledgement — non-toggleable consent rendered at the top
 *     of the public signup comms step. Admin owns the copy; updating
 *     the version label is the audit anchor for "did the member
 *     acknowledge this revision".
 *
 *   • Marketing channels — for IGT venues the list is mirrored from
 *     Aristocrat (`GET /marketing/options` sync), so the venue admin
 *     can only toggle `active`. For S7000 it's fully editable here
 *     since S7000 has no native marketing-prefs concept.
 *
 *   • Marketing reasons (IGT only) — IGT requires a `directMarketingReasonId`
 *     on every PUT. The admin picks one default; per-context overrides
 *     for "new join" and "renewal" let the same venue tell new vs
 *     renewing members apart in Aristocrat reports.
 */
export default function SettingsCommunications() {
  return (
    <ClientLayout>
      <RequireClientAdmin>
        <Inner />
      </RequireClientAdmin>
    </ClientLayout>
  );
}

function Inner() {
  const [venue, setVenue]       = useState(null);
  const [options, setOptions]   = useState([]);
  const [reasons, setReasons]   = useState([]);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");
  const [newOption, setNewOption] = useState("");

  useEffect(() => { reload(); }, []);

  async function reload() {
    setError("");
    try {
      const [v, opts, rs] = await Promise.all([
        clientApi.getVenue(),
        clientApi.getMarketingOptions(),
        clientApi.getMarketingReasons().catch(() => []),
      ]);
      setVenue({
        provider_type:               v.provider_type || "s7000",
        agm_enabled:                 !!v.agm_enabled,
        comms_step_enabled:          v.comms_step_enabled !== false,
        agm_copy:                    v.agm_copy    || "",
        agm_version:                 v.agm_version || "",
        default_marketing_reason_id: v.default_marketing_reason_id ?? "",
        marketing_reason_id_new:     v.marketing_reason_id_new ?? "",
        marketing_reason_id_renewal: v.marketing_reason_id_renewal ?? "",
      });
      setOptions(opts || []);
      setReasons(rs || []);
    } catch (err) { setError(err.message); }
  }

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 2500); };
  const set = (k, v) => setVenue((cur) => ({ ...cur, [k]: v }));

  async function saveVenue() {
    setSaving(true); setError("");
    try {
      const payload = { ...venue };
      // Strip empty-string reason IDs so the column lands as NULL
      // rather than NaN — the backend pulls them through as numbers.
      ["default_marketing_reason_id", "marketing_reason_id_new", "marketing_reason_id_renewal"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      await clientApi.updateVenue(payload);
      flash("Communications settings saved.");
    } catch (err) { setError(err.message); }
    setSaving(false);
  }

  async function addOption() {
    const name = newOption.trim();
    if (!name) return;
    try {
      const created = await clientApi.createMarketingOption({ name, active: true });
      setOptions((cur) => [...cur, created]);
      setNewOption("");
      flash("Option added.");
    } catch (err) { setError(err.message); }
  }

  async function patchOption(id, patch) {
    try {
      const updated = await clientApi.updateMarketingOption(id, patch);
      setOptions((cur) => cur.map((o) => (o.id === id ? updated : o)));
    } catch (err) { setError(err.message); }
  }

  async function deleteOption(id) {
    if (!window.confirm("Delete this marketing option? Existing member opt-ins for it will also be removed.")) return;
    try {
      await clientApi.deleteMarketingOption(id);
      setOptions((cur) => cur.filter((o) => o.id !== id));
      flash("Option deleted.");
    } catch (err) { setError(err.message); }
  }

  if (!venue) return <div style={{ maxWidth: 880 }}><div className="loading-state"><div className="spinner" /></div></div>;

  const isIgt = venue.provider_type === "igt";

  return (
    <div style={{ maxWidth: 880 }}>
      <div className="page-header">
        <h1>Communications</h1>
        <p className="page-subtitle">
          Configure the comms-prefs step shown during public signup and
          renewal — AGM acknowledgement copy and the channels members can
          opt in or out of.
        </p>
      </div>

      {error   && <div className="toast toast-error"   style={{ marginBottom: "1rem" }}>{error}</div>}
      {success && <div className="toast toast-success" style={{ marginBottom: "1rem" }}>{success}</div>}

      {/* ── Show/hide the whole Comms step ────────────────────── */}
      <section style={{ marginBottom: "1.5rem" }}>
        <div className="form-field">
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={venue.comms_step_enabled !== false}
              onChange={(e) => set("comms_step_enabled", e.target.checked)}
            />
            <span>Show the Communications step during public signup</span>
          </label>
          <p className="section-desc" style={{ marginTop: "0.4rem" }}>
            Off = the whole Comms page (AGM acknowledgement + marketing opt-ins)
            is skipped during signup, regardless of the settings below. Save to apply.
          </p>
        </div>
      </section>

      {/* ── AGM ───────────────────────────────────────────────── */}
      <section style={{ marginBottom: "2rem" }}>
        <h2 style={{ marginBottom: "0.4rem" }}>AGM acknowledgement</h2>
        <p className="section-desc">
          Rendered as a non-toggleable, pre-ticked checkbox above the
          marketing options. Members can't opt out. We stamp the
          version on the member record at acknowledgement time — bump
          the version whenever you change the copy materially so the
          audit trail stays meaningful.
        </p>

        <div className="form-field" style={{ marginBottom: "1rem" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={venue.agm_enabled}
              onChange={(e) => set("agm_enabled", e.target.checked)}
            />
            <span>Show AGM acknowledgement on the signup form</span>
          </label>
        </div>

        <div className="form-field" style={{ marginBottom: "0.75rem" }}>
          <label>Version label</label>
          <input
            value={venue.agm_version}
            onChange={(e) => set("agm_version", e.target.value)}
            placeholder="e.g. 2026-05"
            style={{ maxWidth: 200 }}
          />
        </div>

        <div className="form-field" style={{ marginBottom: "0.75rem" }}>
          <label>Copy</label>
          <textarea
            value={venue.agm_copy}
            onChange={(e) => set("agm_copy", e.target.value)}
            placeholder="By joining, I acknowledge that I will receive Annual General Meeting notices from the venue. This notification cannot be disabled."
            rows={5}
            style={{ width: "100%", fontFamily: "inherit", fontSize: "0.95rem" }}
          />
        </div>
      </section>

      {/* ── Marketing reason mapping (IGT only) ───────────────── */}
      {isIgt && (
        <section style={{ marginBottom: "2rem" }}>
          <h2 style={{ marginBottom: "0.4rem" }}>Marketing reason (IGT)</h2>
          <p className="section-desc">
            IGT requires a reason ID with every marketing opt-in
            change. Pick the default we send for this venue. Override
            per join type if you want IGT reports to distinguish new
            joins from renewals.
          </p>

          {reasons.length === 0 ? (
            <div className="toast toast-warning" style={{ marginBottom: "0.75rem" }}>
              No marketing reasons synced yet. Run a full sync from the
              admin panel — comms-prefs PUTs to IGT will be skipped
              until at least one reason is available.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem" }}>
              <ReasonPicker
                label="Default"
                value={venue.default_marketing_reason_id}
                reasons={reasons}
                onChange={(v) => set("default_marketing_reason_id", v)}
              />
              <ReasonPicker
                label="New joins (override)"
                value={venue.marketing_reason_id_new}
                reasons={reasons}
                onChange={(v) => set("marketing_reason_id_new", v)}
              />
              <ReasonPicker
                label="Renewals (override)"
                value={venue.marketing_reason_id_renewal}
                reasons={reasons}
                onChange={(v) => set("marketing_reason_id_renewal", v)}
              />
            </div>
          )}
        </section>
      )}

      <div style={{ marginBottom: "2rem" }}>
        <button className="btn btn-primary" disabled={saving} onClick={saveVenue}>
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>

      {/* ── Export ────────────────────────────────────────────── */}
      <section style={{ marginBottom: "2rem" }}>
        <h2 style={{ marginBottom: "0.4rem" }}>Export opt-ins</h2>
        <p className="section-desc">
          One row per member with a Y / N column per active channel
          plus the AGM acknowledgement timestamp. Use it to compile
          email or SMS distribution lists outside the platform.
        </p>
        <a
          className="btn btn-outline btn-sm"
          href="/api/client/reports/comms-preferences.csv"
          download
        >
          Download CSV
        </a>
      </section>

      {/* ── Marketing channel options ─────────────────────────── */}
      <section>
        <h2 style={{ marginBottom: "0.4rem" }}>Marketing channels</h2>
        <p className="section-desc">
          {isIgt
            ? "Synced from IGT — toggle channels off here to hide them from the signup form without changing the provider. Names are managed in IGT."
            : "Channels you want members to be able to opt in or out of (Email, SMS, Mail, etc.). Stored locally — your provider doesn't expose marketing channels, so the venue defines its own here."}
        </p>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Channel</th>
                <th style={{ width: 100 }}>Source</th>
                <th style={{ width: 110 }}>Active</th>
                <th style={{ width: 80 }}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {options.length === 0 && (
                <tr><td colSpan={4} className="muted">No marketing options configured.</td></tr>
              )}
              {options.map((o) => (
                <tr key={o.id}>
                  <td>{o.name}</td>
                  <td style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
                    {o.source === "igt_sync" ? "IGT" : "Custom"}
                  </td>
                  <td>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={!!o.active}
                        onChange={(e) => patchOption(o.id, { active: e.target.checked })}
                      />
                      <span style={{ fontSize: "0.85rem" }}>{o.active ? "Visible" : "Hidden"}</span>
                    </label>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {o.source === "venue_custom" && (
                      <button className="btn btn-outline btn-sm" onClick={() => deleteOption(o.id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!isIgt && (
          <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
            <input
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              placeholder="New channel name (e.g. Email)"
              style={{ flex: 1 }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
            />
            <button className="btn btn-primary btn-sm" onClick={addOption} disabled={!newOption.trim()}>
              Add channel
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function ReasonPicker({ label, value, reasons, onChange }) {
  return (
    <div className="form-field">
      <label>{label}</label>
      <AdminSelect
        value={value == null ? "" : String(value)}
        onChange={onChange}
        options={[
          { value: "", label: "— Not set —" },
          ...reasons.map((r) => ({
            value: String(r.marketing_reason_id),
            label: `${r.marketing_reason_id} — ${r.name}`,
          })),
        ]}
      />
    </div>
  );
}
